<template lang="pug">
  div(class="billing-overview__card gradient-bg")
    div(class="billing-overview__card-top")
      h4(class="text-16px dark fw-500 pb-1") {{ $t('Your tariff') }}:
        |
        |
        billing-premium-label(v-if="currentPlanName === 'Premium'")
        span(
          v-else
          class="color-primary"
        ) {{ currentPlanName }}
      div(class="text-12px fw-300 pb-6") {{ subtitle }}
      div(class="billing-overview__items")
        div(class="billing-overview__item")
          div(class="text-12px fw-300") {{ $t('Used') }}
          div(class="title-20px fw-500") {{ usedDocumentsCount }}
        div(class="billing-overview__item")
          div(class="text-12px fw-300") {{ $t('Left') }}
          div(
            class="title-20px fw-500 succeed"
            :class="{ errored: reachedLeftLimit }"
          ) {{ leftDocumentsCount }}
        div(class="billing-overview__item")
          div(class="text-12px fw-300") {{ $t('Overall limit') }}
          div(class="title-20px fw-500") {{ availableDocumentsCount }}
    div(class="billing-overview__card-bottom")
      billing-payment-info(:info="planInfo")
      div(
        v-if="isFreePlan || invoice"
        class="billing-overview__actions"
      )
        v-btn(
          class="main-button"
          :loading="updateToPremiumLoading"
          @click="getPremium"
        ) {{ buttonText }}
</template>

<script>
import { mapGetters } from 'vuex'
import BillingPremiumLabel from '~/modules/billing/components/billing-premium-label'
import BillingPaymentInfo from '~/modules/billing/components/billing-payment-info'
import { PLAN_TYPES, FREE_PLAN_ID } from '~/modules/billing/constants/common'
import currencyFormat from '~/mixins/filters/currencyFormat'
import 'moment/locale/uk'
import actions from '~/modules/billing/mixins/actions'

export default {
  name: 'BillingCurrentPlanCard',
  components: {
    BillingPaymentInfo,
    BillingPremiumLabel
  },
  mixins: [currencyFormat, actions],
  computed: {
    ...mapGetters('billing', {
      balance: 'balance',
      invoice: 'invoice',
      currentPlan: 'currentPlan',
      expiresAt: 'expiresAt'
    }),
    currentPlanName() {
      return this._.upperFirst(this._.get(this.currentPlan, 'tariff.name'))
    },
    currentPlanPrice() {
      const subscriptionPrice = this._.get(this.currentPlan, 'tariff.subscriptionPrice', 0) / 100
      return this.handleUahString(subscriptionPrice)
    },
    currentPlanAvailableUntil() {
      const endDate = this._.get(this.currentPlan, 'endDate')
      return this.$moment.parseZone(endDate).locale('uk').format('D MMMM YYYY')
    },
    isFreePlan() {
      return this._.get(this.currentPlan, 'tariff.id') === FREE_PLAN_ID
    },
    subtitle() {
      return this.isFreePlan
        ? this.$t(
            'You are only using part of Dubidoc`s capabilities. Go Premium and work without restrictions'
          )
        : this.$t('Maximum productivity and advanced electronic document management capabilities')
    },
    usedDocumentsCount() {
      return this._.get(this.currentPlan, 'usedDocuments', 0)
    },
    leftDocumentsCount() {
      return this.availableDocumentsCount - this.usedDocumentsCount
    },
    reachedLeftLimit() {
      return this.leftDocumentsCount < this.availableDocumentsCount / 10
    },
    availableDocumentsCount() {
      return this._.get(this.currentPlan, 'availableDocuments', 0)
    },
    formattedBalance() {
      const normalizedBalance = this.balance / 100
      return this.handleUahString(normalizedBalance)
    },
    nextPeriodThirdRow() {
      if (!this.expiresAt || !this.currentPlan?.endDate) return null
      const currentEndDate = new Date(this.currentPlan.endDate).getTime()
      const expiresDate = new Date(this.expiresAt).getTime()
      if (currentEndDate === expiresDate) return null
      const nextActivationDate = this.$moment
        .parseZone(this.expiresAt)
        .locale('uk')
        .format('D MMMM YYYY')
      return {
        label: this.$t('Paid until'),
        value: nextActivationDate,
        tooltip: {
          text: this.$t(
            'The date until which the Dubidoc Premium plan is paid. Limits will reset at the start of the next period'
          )
        }
      }
    },
    planInfo() {
      const baseInfo = {
        firstRow: {
          label: this.$t('Price for tariff'),
          value: this.currentPlanPrice
        },
        secondRow: {
          label: this.$t('Available until'),
          value: this.currentPlanAvailableUntil,
          tooltip: {
            text: this.$t('You documents limit updates every month for plan documents amount', {
              amount: this.availableDocumentsCount
            })
          }
        },
        summaryRow: {
          label: this.$t('Current balance'),
          value: this.formattedBalance
        }
      }
      if (this.nextPeriodThirdRow) {
        baseInfo.thirdRow = this.nextPeriodThirdRow
      }
      return baseInfo
    },
    buttonText() {
      if (this.isFreePlan && this.$Organization?.canActivateTrial) {
        return this.$t('Try for free')
      } else if (this.invoice && !this.isFreePlan) {
        return this.$t('Continue plan')
      }
      return this.$t('Update to Premium')
    }
  },
  methods: {
    async getPremium() {
      if (this.isFreePlan) {
        await this.updateToPremium(false)
      } else if (this.invoice) {
        await this.purchase(PLAN_TYPES.tariff)
      }
    }
  }
}
</script>

<style scoped lang="scss"></style>
